import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ChargerEV, CompanyBuilding } from '../../../assets/icons';
import {
  PromotionFormErrors,
  PromotionFormState,
} from '../../../stores/types/promotionForm.interface';
import { ColorType, Label, LabelType, Pill } from '../../_ui';
import { formatPromotionWindow } from '../promotion.utils';
import {
  ALL_CHARGERS_ID,
  LANGUAGE_LABEL_KEY,
  OPTIONAL_PROMOTION_LANGUAGES,
  PROMOTION_LANGUAGES,
} from './promotionForm.constants';

interface AppliesToPill {
  id: string;
  label: string;
  isLocation: boolean;
}

const getAppliesToPills = (state: PromotionFormState): AppliesToPill[] => {
  const pills: AppliesToPill[] = [];
  state.chargers.forEach((group) => {
    if (group.id === ALL_CHARGERS_ID) return;
    const selectedChildren = group.children.filter((c) => c.selected);
    if (selectedChildren.length === 0) return;
    if (selectedChildren.length === group.children.length) {
      pills.push({ id: group.id, label: group.label, isLocation: true });
    } else {
      selectedChildren.forEach((c) => pills.push({ id: c.id, label: c.label, isLocation: false }));
    }
  });
  return pills;
};

interface Props {
  state: PromotionFormState;
  errors?: PromotionFormErrors;
}

export const PromotionDetailsReadOnly = ({ state, errors }: Props) => {
  const { t, i18n } = useTranslation();

  const appliesToPills = useMemo(() => getAppliesToPills(state), [state]);

  const windowText =
    !state.validFrom && !state.validUntil
      ? '—'
      : formatPromotionWindow(state.validFrom, state.validUntil, {
          locale: i18n.language,
          endFallback: t('no_end_date'),
        });

  const visibleLabelLanguages = [
    PROMOTION_LANGUAGES.ENGLISH,
    ...OPTIONAL_PROMOTION_LANGUAGES.filter((lang) => state.labels[lang]),
  ];

  const renderError = (message?: string) => {
    if (!message) return null;
    return (
      <Label
        className='pt-1'
        type={LabelType.LABEL_S_MEDIUM}
        color={ColorType.NEGATIVE}
        text={message}
      />
    );
  };

  return (
    <div className='flex flex-col divide-y divide-grey2 px-5 bg-white'>
      {/* Promotion Name */}
      <div className='flex flex-col gap-2 py-5'>
        <Label text={t('promotion_name')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
        <Label text={state.name || '—'} type={LabelType.BODY3} color={ColorType.GREY6} />
        {renderError(errors?.name)}
      </div>

      {/* Window */}
      <div className='flex flex-col gap-2 py-5'>
        <Label text={t('window')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
        <Label text={windowText} type={LabelType.BODY3} color={ColorType.GREY6} />
        {renderError(errors?.validFrom)}
        {renderError(errors?.validUntil)}
      </div>

      {/* Value */}
      <div className='flex flex-col gap-2 py-5'>
        <Label text={t('value')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
        <Label
          text={state.discountValue ? `${state.discountValue}% ${t('off')}` : '—'}
          type={LabelType.BODY3}
          color={ColorType.GREY6}
        />
        {renderError(errors?.discountValue)}
      </div>

      {/* Applies To */}
      <div className='flex flex-col gap-2 py-5'>
        <Label text={t('applies_to')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
        <div className='flex flex-row flex-wrap gap-2'>
          {appliesToPills.length === 0 ? (
            <Label text='—' type={LabelType.BODY3} color={ColorType.GREY6} />
          ) : (
            appliesToPills.map((pill) => (
              <Pill
                key={pill.id}
                label={pill.label}
                iconLeft={pill.isLocation ? CompanyBuilding : ChargerEV}
              />
            ))
          )}
        </div>
        {renderError(errors?.chargers)}
      </div>

      {/* Receipt Label */}
      <div className='flex flex-col gap-3 py-5'>
        <Label text={t('receipt_label')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
        {visibleLabelLanguages.map((lang) => (
          <div key={lang} className='flex flex-col gap-1'>
            <Label
              text={t(LANGUAGE_LABEL_KEY[lang])}
              type={LabelType.LABEL_S}
              color={ColorType.GREY6}
            />
            <Label
              text={state.labels[lang] || '—'}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
